import axios from "axios";

/** Clean the configured backend URL, or throw with a message that names the fix.
 *
 *  This matters more as a static site than it did served from one box. Create React App
 *  inlines REACT_APP_* at BUILD time, so a static site built without the variable ships a
 *  bundle containing the literal string "undefined": every call goes to `undefined/api/…`,
 *  resolved against the site's own origin, and every screen reports a network error with
 *  nothing anywhere saying the build was misconfigured. Rebuilding is the only fix, which
 *  is exactly the thing you want to be told at once.
 *
 *  The checks are the ones the mobile app's `usableUrl()` already learned the hard way:
 *  a value must be a STRING (tooling has turned this kind of setting into `{}`, which is
 *  truthy and stringifies to "[object Object]") and it must carry a scheme.
 */
export function apiBaseUrl(raw) {
  if (typeof raw !== "string" || !raw.trim()) {
    throw new Error(
      "REACT_APP_BACKEND_URL is not set. It is read at build time, so a static site " +
      "must have it in the build environment and then be rebuilt."
    );
  }
  const value = raw.trim().replace(/\/+$/, "");
  if (!/^https?:\/\/[^/\s]+/.test(value)) {
    throw new Error(
      `REACT_APP_BACKEND_URL must be a full http(s) URL, not ${JSON.stringify(raw)}.`
    );
  }
  if (/\/api$/i.test(value)) {
    throw new Error(
      "REACT_APP_BACKEND_URL must NOT end in /api — this client appends it, so that " +
      "would request /api/api."
    );
  }
  return value;
}

/** Module-level resolution. Deliberately does not throw.
 *
 *  An exception at import takes the whole bundle down, and with it the only surface left
 *  that could tell anyone what is wrong. Falling back to the site's own origin makes the
 *  failure a plain 404 with a named explanation beside it in the console.
 */
function resolveBackendUrl() {
  try {
    return apiBaseUrl(process.env.REACT_APP_BACKEND_URL);
  } catch (e) {
    console.error(`RelationshipOS: ${e.message}`);
    return "";
  }
}

const BACKEND_URL = resolveBackendUrl();

// `withCredentials` is what carries the auth cookie, and on a cross-SITE deployment it is
// only half the story: the cookie also has to go out SameSite=None; Secure from the
// backend, or the browser holds it back and every call 401s. See DEPLOYMENT.md.
export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

/** Which portal a path belongs to.
 *
 *  Match whole path segments, never bare prefixes. "/patients" and "/patients/:id" are
 *  the CLINIC's patient list and record — staff routes — and a startsWith("/patient")
 *  test swallows both, so every request from the two busiest staff screens went out
 *  labelled as the patient portal. With a patient also signed in that resolves to the
 *  patient's session and the clinic's own patient list answers
 *  "You do not have permission for this action".
 */
export function portalForPath(path) {
  const patient = path === "/portal" || path.startsWith("/portal/")
               || path === "/patient" || path.startsWith("/patient/");
  return patient ? "patient" : "staff";
}

/** Which portal this tab is currently showing. */
export function currentPortal() {
  return portalForPath(window.location.pathname);
}

// Staff and patient sessions now hold separate cookies so both can be signed in at once
// in the same browser. Shared endpoints (/auth/me, /availability, /clinic/branding) would
// otherwise have no way to tell which of the two a tab means, and the answer differs.
api.interceptors.request.use((config) => {
  config.headers["X-Portal"] = currentPortal();
  return config;
});

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/** Just the digits — what actually gets stored. */
export function digitsOnly(value) {
  return String(value ?? "").replace(/\D/g, "");
}

/** Display a US phone number as (555) 555-5555.
 *
 *  Storage is digits; formatting is a rendering concern, so this is the single place it
 *  happens. Anything that is not a recognisable 10-digit US number comes back untouched:
 *  a half-formatted "(123) 45-" would hide what the user actually typed and quietly imply
 *  the record holds a real number.
 */
export function formatPhone(value) {
  if (value == null || value === "") return "";
  let d = digitsOnly(value);
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
  if (d.length !== 10) return String(value);
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export const ROLE_LABELS = {
  owner: "Clinic Owner",
  provider: "Provider",
  nurse: "Nurse / MA",
  front_desk: "Front Desk",
  system_admin: "System Admin",
};
